import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import '../GetLabelsPage.css';
import ReplacementLabel from './ReplacementLabel';

// ── PDF helpers ───────────────────────────────────────────────────────────────

function formatBagNumber(n) {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const idx = Math.floor((n - 1) / 9999);
  const num = ((n - 1) % 9999) + 1;
  return `${letters[idx] || 'Z'}${String(num).padStart(4, '0')}`;
}

async function qrToDataUrl(text) {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, text, { margin: 1, width: 220, errorCorrectionLevel: 'M' });
  return canvas.toDataURL('image/png');
}

const LH = (pt) => pt * 0.3528 * 1.08;

// Label: BAG # + number
async function generateBagNumberPDF(labelCount, startNumber) {
  const W = 50;
  const H = 25;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [W, H] });

  const qrMargin = 0.75;
  const qrSize   = H - 2 * qrMargin;
  const qrX      = W - qrSize - qrMargin;
  const qrY      = qrMargin;
  const colW     = qrX - 0.5;
  const colCX    = colW / 2;

  const fontSize = 16;
  const lineH    = fontSize * 0.3528 * 1.1;
  const blockH   = lineH * 2;
  const startY   = (H - blockH) / 2 + lineH;

  for (let i = 0; i < labelCount; i++) {
    if (i > 0) pdf.addPage([W, H], 'landscape');
    const bagNum    = formatBagNumber(startNumber + i);
    const qrDataUrl = await qrToDataUrl(bagNum);
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    pdf.text('BAG #', colCX, startY,          { align: 'center' });
    pdf.text(bagNum,   colCX, startY + lineH, { align: 'center' });
  }

  return pdf;
}

// Label: seed name + QR with seedsID
async function generateSeedIdPDF(seedName, seedsID, qty) {
  const W = 50;
  const H = 25;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [W, H] });

  const qrMargin  = 0.75;
  const qrSize    = H - 2 * qrMargin;
  const qrX       = W - qrSize - qrMargin;
  const qrY       = qrMargin;
  const colW      = qrX - 0.5;
  const colCX     = colW / 2;
  const textW     = colW - 1;
  const availH    = H - 2;

  const qrDataUrl = await qrToDataUrl(String(seedsID));

  let namePt = 14;
  let nameLines;
  while (namePt >= 7) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(namePt);
    nameLines = pdf.splitTextToSize(seedName, textW);
    if (nameLines.length * LH(namePt) <= availH) break;
    namePt -= 0.5;
  }

  const nameTH = nameLines.length * LH(namePt);
  const baseY  = (H - nameTH) / 2 + LH(namePt);

  for (let i = 0; i < qty; i++) {
    if (i > 0) pdf.addPage([W, H], 'landscape');
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(namePt);
    nameLines.forEach((line, idx) => {
      pdf.text(line, colCX, baseY + idx * LH(namePt), { align: 'center' });
    });
  }

  return pdf;
}

// ── Component ─────────────────────────────────────────────────────────────────

const SeedBags = () => {
  // ── Section 1: Bag numbering ──────────────────────────────────────────────
  const [bagQty,        setBagQty]        = useState(1);
  const [generatingBag, setGeneratingBag] = useState(false);
  const [bagProgress,   setBagProgress]   = useState(0);
  const [bagFile,       setBagFile]       = useState(null);
  const [bagError,      setBagError]      = useState('');

  const handleGenerateBag = async () => {
    const count = parseInt(bagQty) || 0;
    if (count < 1) return;
    setGeneratingBag(true);
    setBagProgress(0);
    setBagFile(null);
    setBagError('');
    try {
      const res = await api.post('/api/labels/reserve-seed-bags', { count });
      const { startNumber } = res.data.data;
      setBagProgress(30);
      const pdf  = await generateBagNumberPDF(count, startNumber);
      setBagProgress(100);
      const blob = pdf.output('blob');
      const name = count === 1
        ? `Bag ${formatBagNumber(startNumber)}`
        : `Bags ${formatBagNumber(startNumber)}–${formatBagNumber(startNumber + count - 1)}`;
      setBagFile({ blob, name });
    } catch (err) {
      setBagError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingBag(false);
    }
  };

  const downloadBagFile = () => {
    const url = URL.createObjectURL(bagFile.blob);
    const a   = document.createElement('a');
    a.href = url; a.download = `${bagFile.name}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Section 2: Seed ID labels ─────────────────────────────────────────────
  const [seeds,          setSeeds]          = useState([]);
  const [seedSearch,     setSeedSearch]     = useState('');
  const [showSeedDrop,   setShowSeedDrop]   = useState(false);
  const [selectedSeeds,  setSelectedSeeds]  = useState([]);
  const [generatingSeed, setGeneratingSeed] = useState(false);
  const [seedProgress,   setSeedProgress]   = useState(0);
  const [seedProgLabel,  setSeedProgLabel]  = useState('');
  const [seedFiles,      setSeedFiles]      = useState([]);
  const [seedError,      setSeedError]      = useState('');

  const seedSearchRef   = useRef(null);
  const seedSearchInput = useRef(null);

  useEffect(() => {
    api.get('/api/labels/seeds')
      .then(r => setSeeds(r.data.data))
      .catch(() => setSeedError('Failed to load seeds'));
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (seedSearchRef.current && !seedSearchRef.current.contains(e.target))
        setShowSeedDrop(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filteredSeeds = seeds.filter(
    (s) =>
      !selectedSeeds.find((i) => i.seedsID === s.seedsID) &&
      ((s.name || '').toLowerCase().includes(seedSearch.toLowerCase()) ||
        String(s.seedsID).includes(seedSearch))
  );

  const addSeed = (seed) => {
    setSelectedSeeds((prev) => [...prev, { ...seed, qty: 1 }]);
    setSeedSearch('');
    setShowSeedDrop(true);
    seedSearchInput.current?.focus();
  };

  const updateSeed = (seedsID, qty) =>
    setSelectedSeeds((prev) => prev.map((s) => s.seedsID === seedsID ? { ...s, qty } : s));

  const removeSeed = (seedsID) =>
    setSelectedSeeds((prev) => prev.filter((s) => s.seedsID !== seedsID));

  const handleGenerateSeed = async () => {
    if (selectedSeeds.length === 0) return;
    setGeneratingSeed(true);
    setSeedProgress(0);
    setSeedProgLabel('');
    setSeedFiles([]);
    setSeedError('');
    try {
      const files = [];
      for (let idx = 0; idx < selectedSeeds.length; idx++) {
        const item = selectedSeeds[idx];
        setSeedProgLabel(`Generating: ${item.name}`);
        const pdf  = await generateSeedIdPDF(item.name, item.seedsID, item.qty);
        const blob = pdf.output('blob');
        files.push({ name: item.name, seedsID: item.seedsID, blob });
        setSeedProgress(Math.round(((idx + 1) / selectedSeeds.length) * 100));
      }
      setSeedFiles(files);
    } catch (err) {
      setSeedError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingSeed(false);
      setSeedProgLabel('');
    }
  };

  const downloadSeedFile = (file) => {
    const url = URL.createObjectURL(file.blob);
    const a   = document.createElement('a');
    a.href = url; a.download = `${file.name}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllSeed = () => seedFiles.forEach(downloadSeedFile);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="gl-content">

      {/* ── Section 1: Bag Numbering ── */}
      <div className="gl-divider" style={{ marginTop: 0, marginBottom: 28 }}>Bag Numbering Labels</div>

      {bagError && <div className="gl-error">{bagError}</div>}

      <div className="gl-seed-qty-row">
        <div className="gl-qty-field">
          <label className="gl-label">Number of labels</label>
          <input
            type="number" min="1"
            className="gl-input-lg"
            value={bagQty}
            onChange={(e) => { setBagQty(e.target.value); setBagFile(null); }}
          />
        </div>
        {!generatingBag && !bagFile && (
          <button
            className="gl-btn gl-btn-primary"
            onClick={handleGenerateBag}
            disabled={!bagQty || parseInt(bagQty) < 1}
          >
            Generate Labels
          </button>
        )}
      </div>

      {generatingBag && (
        <div className="gl-progress-wrap">
          <div className="gl-progress-label">Generating… {bagProgress}%</div>
          <div className="gl-progress-track">
            <div className="gl-progress-fill" style={{ width: `${bagProgress}%` }} />
          </div>
        </div>
      )}

      {bagFile && (
        <div className="gl-done-header">
          <span className="gl-done-label">Ready!</span>
          <button className="gl-btn gl-btn-primary" onClick={downloadBagFile}>Download PDF</button>
          <button className="gl-btn gl-btn-gray" onClick={() => setBagFile(null)}>New Batch</button>
        </div>
      )}

      <ReplacementLabel
        numberLabel="Bag number"
        fileName={(code) => `Bag ${code} (replacement)`}
        generate={(n) => generateBagNumberPDF(1, n)}
      />

      {/* ── Section 2: Seed ID Labels ── */}
      <div className="gl-divider" style={{ margin: '32px 0 28px' }}>Seed ID Labels</div>

      {seedError && <div className="gl-error">{seedError}</div>}

      {/* Seed search */}
      <div className="gl-search-wrap" ref={seedSearchRef}>
        <label className="gl-label">Add Seeds</label>
        <input
          ref={seedSearchInput}
          type="text"
          className="gl-input"
          value={seedSearch}
          onChange={(e) => { setSeedSearch(e.target.value); setShowSeedDrop(true); }}
          onFocus={() => setShowSeedDrop(true)}
          placeholder="Search by name or ID — click to add, search again for more…"
        />
        {showSeedDrop && (seedSearch || filteredSeeds.length > 0) && (
          <div className="gl-dropdown">
            {filteredSeeds.length === 0 ? (
              <div className="gl-dropdown-empty">
                {seedSearch ? 'No seeds found' : 'Start typing to search…'}
              </div>
            ) : (
              filteredSeeds.map((s) => (
                <div
                  key={s.seedsID}
                  className="gl-dropdown-item"
                  onMouseDown={() => addSeed(s)}
                >
                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>#{s.seedsID}</span>
                  {s.categoryName && (
                    <span className="gl-source-badge">{s.categoryName}</span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Selected seeds table */}
      {selectedSeeds.length > 0 && (
        <div className="gl-table-wrap">
          <table className="gl-table">
            <thead>
              <tr>
                <th className="gl-th">Seed</th>
                <th className="gl-th gl-th-w110">ID</th>
                <th className="gl-th gl-th-w120 gl-th-center">Label Count</th>
                <th className="gl-th gl-th-w40 gl-th-center" />
              </tr>
            </thead>
            <tbody>
              {selectedSeeds.map((item) => (
                <tr key={item.seedsID}>
                  <td className="gl-td">{item.name}</td>
                  <td className="gl-td gl-td-muted">#{item.seedsID}</td>
                  <td className="gl-td gl-td-center">
                    <input
                      type="number" min="1"
                      className="gl-num-input"
                      value={item.qty}
                      onChange={(e) => updateSeed(item.seedsID, parseInt(e.target.value) || 1)}
                    />
                  </td>
                  <td className="gl-td gl-td-center">
                    <button className="gl-remove-btn" onClick={() => removeSeed(item.seedsID)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedSeeds.length > 0 && !generatingSeed && seedFiles.length === 0 && (
        <button className="gl-btn gl-btn-primary gl-btn-generate" onClick={handleGenerateSeed}>
          Generate Labels
        </button>
      )}

      {generatingSeed && (
        <div className="gl-progress-wrap">
          <div className="gl-progress-label">
            {seedProgLabel || 'Generating…'} — {seedProgress}%
          </div>
          <div className="gl-progress-track">
            <div className="gl-progress-fill" style={{ width: `${seedProgress}%` }} />
          </div>
        </div>
      )}

      {seedFiles.length > 0 && (
        <div>
          <div className="gl-done-header">
            <span className="gl-done-label">
              Done! {seedFiles.length} file{seedFiles.length > 1 ? 's' : ''} ready
            </span>
            {seedFiles.length > 1 && (
              <button className="gl-btn gl-btn-primary" onClick={downloadAllSeed}>Download All</button>
            )}
            <button className="gl-btn gl-btn-gray" onClick={() => { setSeedFiles([]); setSeedProgress(0); }}>
              New Batch
            </button>
          </div>
          <div className="gl-files-list">
            {seedFiles.map((f, i) => (
              <div key={i} className="gl-file-item">
                <div>
                  <span className="gl-file-name">{f.name}</span>
                  <span className="gl-file-sku">#{f.seedsID}</span>
                </div>
                <button className="gl-btn gl-btn-primary gl-btn-sm" onClick={() => downloadSeedFile(f)}>
                  Download PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default SeedBags;
